import { useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { IconRefresh, IconStop } from '@/components/Icons';
import { PROFILES, profileById } from '@/data/catalog';
import { buildHoldingsContext, PRESET_IDS, presetLabel, presetText } from '@/experts/context';
import { buildRounds, roundLabel, type CardVM, type RoundVM } from '@/experts/rounds';
import { retryCard, startRound, stopRound } from '@/experts/run';
import { pickLocalized, useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { Markdown } from '@/lib/markdown';
import { visibleOpinions } from '@/lib/opinions';
import { describeError } from '@/llm/types';
import { hasLLM, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import { MAX_PROFILES_PER_THREAD, MIN_CONFIDENCE_DISPLAY, type Opinion, type Thread } from '@/types';
import s from './experts.module.css';

const EMPTY: never[] = [];
const DEFAULT_PICK = ['moat_value', 'cycle_risk', 'growth_momentum'];
const CONTEXT_SPLIT = '\n\n——\n';

export function ExpertsView() {
  const { t, locale } = useT();
  const { threadId } = useParams();
  const app = useAppState();
  const settings = useSettings();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<string[]>(DEFAULT_PICK);
  const [infoOpen, setInfoOpen] = useState<ReadonlySet<string>>(() => new Set());
  const [draft, setDraft] = useState('');

  const thread = threadId ? app.threads.find((x) => x.id === threadId) : undefined;
  const messages = (thread && app.messages[thread.id]) ?? EMPTY;
  const run = thread ? app.runs[thread.id] : undefined;
  const busy = !!run;

  useEffect(() => {
    if (thread) void appStore.ensureMessages(thread.id);
  }, [thread?.id]);

  const context = useMemo(() => buildHoldingsContext(app.assets, app.quotes, app.fx), [app.assets, app.quotes, app.fx, locale]);
  const rounds = useMemo(() => buildRounds(messages, thread?.profileIds ?? EMPTY, (id) => ({ id, label: id })), [messages, thread?.profileIds, locale]);
  const lastRound = rounds[rounds.length - 1];
  const canDebate = !!lastRound && lastRound.cards.filter((c) => c.status === 'complete').length >= 2;
  const llmReady = hasLLM(settings);
  const participants = thread?.profileIds ?? selected;

  const guard = (work: () => Promise<void>) => work().catch((e: unknown) => appStore.notify(describeError(e), 'error'));

  const begin = (question: string) => {
    if (!llmReady) {
      appStore.notify(t('experts.noLLM'));
      return;
    }
    if (selected.length === 0) return;
    guard(async () => {
      const created: Thread = { id: crypto.randomUUID(), title: t('experts.untitled'), domainId: 'fin', profileIds: selected, createdAt: Date.now(), updatedAt: Date.now() };
      await appStore.createThread(created);
      navigate(`/experts/${created.id}`, { replace: true });
      await startRound(created, { type: 'answer', text: `${question}\n\n${t('experts.contextIntro')}\n${context}` });
    });
  };

  const followUp = () => {
    const text = draft.trim();
    if (!thread || !text || busy) return;
    setDraft('');
    guard(() => startRound(thread, { type: 'answer', text }));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      followUp();
    }
  };

  const toggleProfile = (id: string) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX_PROFILES_PER_THREAD ? cur : [...cur, id]));

  return (
    <div className="page">
      <Header crumbs={[{ label: t('header.list'), to: '/' }]} title={t('experts.title')} showCurrency={false} actions={
        <div className={s.threads}>
          {app.threads.length > 0 && (
            <select className="input" style={{ padding: '6px 10px', width: 'auto' }} value={thread?.id ?? ''} onChange={(e) => navigate(e.target.value ? `/experts/${e.target.value}` : '/experts')}>
              <option value="">{t('experts.new')}</option>
              {app.threads.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
            </select>
          )}
          {thread && <button type="button" className="btn quiet" onClick={() => { if (window.confirm(t('experts.confirmDelete'))) { void appStore.deleteThread(thread.id); navigate('/experts'); } }}>{t('experts.delete')}</button>}
        </div>
      } />

      <section className={s.layout}>
        <aside className={s.aside}>
          <div>
            <div className="label">{t('experts.context')}</div>
            <div className={s.context}>{context}</div>
          </div>
          <div>
            <div className="label">{t('experts.pick', { n: participants.length, max: MAX_PROFILES_PER_THREAD })}</div>
            <p className={`muted ${s.pickHint}`}>{t('experts.pickHint')}</p>
            {/* One row per style. Selection is shown three ways — checkbox, solid border, colour bar — because a single
                CJK badge with or without colour was too easy to miss. "Info" opens the description and bibliography. */}
            <div className={s.styleList} role="group" aria-label={t('experts.pickAria')}>
              {PROFILES.map((p) => {
                const on = participants.includes(p.id);
                const open = infoOpen.has(p.id);
                return (
                  <div key={p.id} className={cx(s.styleRow, on && 'on')} style={{ '--style-color': p.color } as CSSProperties}>
                    <button type="button" className={s.styleToggle} role="checkbox" aria-checked={on} disabled={!!thread} onClick={() => toggleProfile(p.id)}>
                      <span className={cx(s.check, on && 'on')} aria-hidden="true">{on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}</span>
                      <i className={s.ini} style={{ background: p.color }}>{pickLocalized(p.badge, locale)}</i>
                      <span className={s.styleText}>
                        <span className={s.styleHead}><span className={`serif ${s.styleName}`}>{pickLocalized(p.name, locale)}</span><span className={`muted ${s.styleField}`}>{pickLocalized(p.field, locale)}</span></span>
                        <span className={`muted ${s.styleTag}`}>{pickLocalized(p.tagline, locale)}</span>
                      </span>
                    </button>
                    <button type="button" className={cx(s.infoBtn, open && 'on')} aria-expanded={open} onClick={() => setInfoOpen((cur) => { const next = new Set(cur); if (next.has(p.id)) next.delete(p.id); else next.add(p.id); return next; })}>{open ? t('experts.infoClose') : t('experts.info')}</button>
                    {open && (
                      <div className={s.styleInfo}>
                        <p className={s.aboutText}>{pickLocalized(p.description, locale)}</p>
                        {/* Bibliography — published works only; the notice under the list keeps it a citation, not an endorsement. */}
                        {p.references.length > 0 && <p className={`muted ${s.aboutRefs}`}>{t('experts.references')}{p.references.map((r) => [r.author, r.work, r.year].filter(Boolean).join(', ')).join('；')}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {thread && <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>{t('experts.fixed')}</p>}
            {/* Styles, not people: the profiles follow public schools of investing and speak for no real person or firm. */}
            <p className={`muted ${s.aboutNote}`}>{infoOpen.size > 0 ? t('experts.referencesNote') : t('experts.disclaimer')}</p>
          </div>
          <div>
            <div className="label">{t('experts.presets')}</div>
            <div className={s.presets}>
              {PRESET_IDS.map((id) => (
                <button key={id} type="button" className={s.preset} disabled={!!thread && busy} onClick={() => (thread ? guard(() => startRound(thread, { type: 'answer', text: presetText(id) })) : begin(presetText(id)))}>{presetLabel(id)}</button>
              ))}
            </div>
          </div>
        </aside>

        <div className={s.main}>
          {!thread && (
            <div className={s.startBox}>
              <h3 className="serif" style={{ fontSize: 22 }}>{t('experts.startTitle')}</h3>
              <p className="muted" style={{ margin: 0, lineHeight: 1.6 }}>
                {t('experts.startBody')}
                {!llmReady && <> {t('experts.needKey')} <Link to="/settings">{t('experts.goSettings')}</Link></>}
              </p>
              <div className={s.composer} style={{ borderTop: 0, paddingTop: 0 }}>
                <textarea className={`input ${s.textarea}`} placeholder={t('experts.askPlaceholder')} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (draft.trim()) { begin(draft.trim()); setDraft(''); } } }} />
                <button type="button" className="btn primary" disabled={!draft.trim() || selected.length === 0} onClick={() => { begin(draft.trim()); setDraft(''); }}>{t('common.send')}</button>
              </div>
            </div>
          )}

          {thread && rounds.map((r) => <RoundSection key={r.round} round={r} onRetry={(id) => guard(() => retryCard(thread, id))} />)}

          {thread && rounds.length > 0 && !busy && (
            <div className={s.footer}>
              <button type="button" className="btn primary" disabled={!canDebate || !llmReady} onClick={() => guard(() => startRound(thread, { type: 'debate' }))} title={canDebate ? undefined : t('experts.needTwo')}>{t('experts.startDebate')}</button>
              <span className="muted" style={{ fontSize: 12 }}>{t('experts.debateHint')}</span>
            </div>
          )}
          {thread && busy && (
            <div className={s.footer}>
              <button type="button" className="btn quiet" onClick={() => stopRound(thread.id)}><IconStop size={12} />{t('common.stop')}</button>
              <span className="muted" style={{ fontSize: 12 }}>{t('experts.progress', { done: run?.done ?? 0, total: run?.total ?? 0 })}</span>
            </div>
          )}

          {thread && (
            <div className={s.composer}>
              <textarea className={`input ${s.textarea}`} placeholder={t('experts.followUp')} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} />
              <button type="button" className="btn primary" disabled={!draft.trim() || busy} onClick={followUp}>{t('common.send')}</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function RoundSection({ round: r, onRetry }: { round: RoundVM; onRetry: (id: string) => void }) {
  const { t, locale } = useT();
  const [filter, setFilter] = useState('all');
  const picked = filter === 'all' ? r.cards : r.cards.filter((c) => c.profile.id === filter);
  const cards = picked.length ? picked : r.cards;
  return (
    <section className={s.round}>
      <div className={s.divider}><i /><span>{roundLabel(r)}{r.type === 'answer' ? t('experts.parallel', { n: r.cards.length }) : ''}</span><i /></div>
      {r.user && (
        <div className={s.userRow}>
          <div className={s.userBubble}>
            <div className="label">{t('experts.me')} · {r.user.time}</div>
            <p>{r.user.text.split(CONTEXT_SPLIT)[0]}</p>
            {r.user.text.includes(CONTEXT_SPLIT) && <p className="muted" style={{ fontSize: 12 }}>{t('experts.withContext')}</p>}
          </div>
        </div>
      )}
      {r.cards.length > 1 && (
        <div className={s.strip} role="tablist">
          <button type="button" role="tab" aria-selected={filter === 'all'} className={cx(s.tab, filter === 'all' && 'on')} onClick={() => setFilter('all')}>{t('experts.all')}</button>
          {r.cards.map((c) => (
            <button key={c.messageId} type="button" role="tab" aria-selected={filter === c.profile.id} className={cx(s.tab, filter === c.profile.id && 'on')} onClick={() => setFilter(c.profile.id)}>
              <i className={s.ini} style={{ background: profileById(c.profile.id)?.color }}>{pickLocalized(c.profile.badge, locale)}</i>
              {pickLocalized(c.profile.name, locale)}
              <span className={`num ${s.tabMeta}`}>{c.status === 'complete' ? visibleOpinions(c.opinions).length : c.status === 'streaming' ? '…' : '!'}</span>
            </button>
          ))}
        </div>
      )}
      <div className={s.ledger}>
        {cards.map((c) => <ExpertSection key={c.messageId} card={c} onRetry={onRetry} />)}
      </div>
    </section>
  );
}

function ExpertSection({ card, onRetry }: { card: CardVM; onRetry: (id: string) => void }) {
  const { t, locale } = useT();
  const shown = visibleOpinions(card.opinions);
  const hidden = card.opinions.length - shown.length;
  const profile = profileById(card.profile.id);
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
  const expandable = shown.map((o, i) => (o.analysis ? i : -1)).filter((i) => i >= 0);
  const allOpen = expandable.length > 0 && expandable.every((i) => open.has(i));
  const toggle = (i: number) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  return (
    <article className={s.expert}>
      <header className={s.expertHead}>
        <i className={s.ini} style={{ background: profile?.color }}>{pickLocalized(card.profile.badge, locale)}</i>
        <span className={`serif ${s.expertName}`} title={pickLocalized(card.profile.description, locale)}>{pickLocalized(card.profile.name, locale)}</span>
        {card.status === 'complete' && <span className="muted" style={{ fontSize: 11 }}>{t('experts.opinions', { n: shown.length })}</span>}
        {card.status === 'streaming' && <span className="badge"><i className="dot delay" />{t('experts.generating')}</span>}
        {card.status === 'error' && <span className="badge"><i className="dot off" />{t('experts.failed')}</span>}
        {card.status === 'complete' && expandable.length > 0 && (
          <button type="button" className={cx(s.toggle, s.headToggle)} onClick={() => setOpen(allOpen ? new Set() : new Set(expandable))}>{allOpen ? t('experts.collapseAll') : t('experts.expandAll')}</button>
        )}
      </header>
      {card.status === 'complete' && (
        <>
          {shown.map((op, i) => <OpinionRow key={i} index={i} opinion={op} open={open.has(i)} onToggle={() => toggle(i)} />)}
          {hidden > 0 && <div className="muted" style={{ padding: '8px 16px', fontSize: 11, borderTop: '1px solid var(--rule-soft)' }}>{t('experts.hidden', { n: hidden, pct: Math.round(MIN_CONFIDENCE_DISPLAY * 100) })}</div>}
        </>
      )}
      {card.status === 'streaming' && <div className={s.streaming}>{card.partial}<span style={{ color: 'var(--link)' }}>▍</span></div>}
      {card.status === 'error' && (
        <div className={s.error}>
          <span className="down">{card.error}</span>
          <button type="button" className="btn quiet" style={{ alignSelf: 'flex-start' }} onClick={() => onRetry(card.messageId)}><IconRefresh size={12} />{t('common.retry')}</button>
        </div>
      )}
    </article>
  );
}

function OpinionRow({ index, opinion, open, onToggle }: { index: number; opinion: Opinion; open: boolean; onToggle: () => void }) {
  const { t } = useT();
  const pct = Math.round(opinion.confidence * 100);
  return (
    <div className={s.op}>
      <span className={`serif muted ${s.opNo}`}>{String(index + 1).padStart(2, '0')}</span>
      <div className={s.opBody}>
        <p className={s.statement}>{opinion.statement}</p>
        {opinion.analysis && <button type="button" className={s.toggle} onClick={onToggle}>{open ? t('experts.collapse') : t('experts.expand')}</button>}
        {open && opinion.analysis && <div className={s.analysis}><Markdown text={opinion.analysis} /></div>}
      </div>
      <div>
        <div className={`serif num ${s.pct}`}>{pct}%</div>
        <div className={s.bar}><i style={{ width: `${pct}%` }} /></div>
      </div>
    </div>
  );
}
